import type { RpcInterceptor } from "@protobuf-ts/runtime-rpc"

/**
 * Create an auth interceptor that adds a Bearer token to all requests
 *
 * @param getToken - Function that returns the auth token (can be async)
 * @returns RpcInterceptor
 *
 * @example
 * ```ts
 * import { createAuthInterceptor, createTransport } from '@fermata/grpc-zustand/runtime'
 *
 * const transport = createTransport({
 *   baseUrl: process.env.NEXT_PUBLIC_GRPC_ENDPOINT!,
 *   interceptors: [
 *     createAuthInterceptor(() => localStorage.getItem('auth_token'))
 *   ]
 * })
 * ```
 */
export function createAuthInterceptor(
  getToken: () => string | null | Promise<string | null>
): RpcInterceptor {
  // Shared for both call kinds. IMPORTANT: this MUST be applied for
  // server-streaming too. protobuf-ts's stackIntercept filters interceptors by
  // call kind (interceptUnary vs interceptServerStreaming), so an interceptor
  // that only implements interceptUnary is silently dropped from streaming
  // calls — which would send StreamTelemetry with no Authorization header.
  const applyToken = (options: { meta?: unknown }): void => {
    // Get token (handle both sync and async)
    const tokenOrPromise = getToken()

    if (tokenOrPromise instanceof Promise) {
      // Note: async token retrieval cannot reliably affect the *current* request
      // because the interceptor must return synchronously.
      // For now, we only apply async tokens to *future* requests.
      tokenOrPromise.then((token) => {
        if (token) {
          setMetaHeader(options, "Authorization", `Bearer ${token}`)
        }
      })
    } else if (tokenOrPromise) {
      // Sync token - add immediately
      setMetaHeader(options, "Authorization", `Bearer ${tokenOrPromise}`)
    }
  }

  return {
    interceptUnary(next, method, input, options) {
      applyToken(options)
      return next(method, input, options)
    },
    interceptServerStreaming(next, method, input, options) {
      applyToken(options)
      return next(method, input, options)
    },
  }
}

/**
 * Create a logging interceptor that logs all gRPC calls
 *
 * @param logger - Optional custom logger (defaults to console)
 * @returns RpcInterceptor
 *
 * @example
 * ```ts
 * import { createLoggingInterceptor, createTransport } from '@fermata/grpc-zustand/runtime'
 *
 * const transport = createTransport({
 *   baseUrl: process.env.NEXT_PUBLIC_GRPC_ENDPOINT!,
 *   interceptors: [createLoggingInterceptor()]
 * })
 * ```
 */
export function createLoggingInterceptor(
  logger: Pick<Console, "log" | "error"> = console
): RpcInterceptor {
  return {
    interceptUnary(next, method, input, options) {
      const serviceName = method.service.typeName
      const methodName = method.name

      logger.log(`[gRPC] ${serviceName}.${methodName}`, input)

      const call = next(method, input, options)

      // Log response when it completes
      call.then(
        (response) => {
          logger.log(`[gRPC] ${serviceName}.${methodName} → success`, response)
        },
        (error) => {
          logger.error(`[gRPC] ${serviceName}.${methodName} → error`, error)
        }
      )

      return call
    },
    interceptServerStreaming(next, method, input, options) {
      const serviceName = method.service.typeName
      const methodName = method.name

      logger.log(`[gRPC stream] ${serviceName}.${methodName}`, input)

      const call = next(method, input, options)
      // ServerStreamingCall isn't a thenable for the response value; log
      // terminal status when the stream settles.
      call.status.then(
        (status) => logger.log(`[gRPC stream] ${serviceName}.${methodName} → ${status.code}`),
        (error) => logger.error(`[gRPC stream] ${serviceName}.${methodName} → error`, error)
      )

      return call
    },
  }
}

/**
 * Create a custom headers interceptor
 *
 * @param getHeaders - Function that returns headers to add
 * @returns RpcInterceptor
 *
 * @example
 * ```ts
 * import { createHeadersInterceptor, createTransport } from '@fermata/grpc-zustand/runtime'
 *
 * const transport = createTransport({
 *   baseUrl: process.env.NEXT_PUBLIC_GRPC_ENDPOINT!,
 *   interceptors: [
 *     createHeadersInterceptor(() => ({
 *       'X-Client-Version': '1.0.0',
 *       'X-Request-ID': crypto.randomUUID()
 *     }))
 *   ]
 * })
 * ```
 */
export function createHeadersInterceptor(getHeaders: () => Record<string, string>): RpcInterceptor {
  // Applied for both call kinds — see the note in createAuthInterceptor about
  // why server-streaming needs its own hook (this interceptor also carries the
  // Authorization header in the web app, so dropping it on streams breaks auth).
  const applyHeaders = (options: { meta?: unknown }): void => {
    const headers = getHeaders()

    for (const [key, value] of Object.entries(headers)) {
      setMetaHeader(options, key, value)
    }
  }

  return {
    interceptUnary(next, method, input, options) {
      applyHeaders(options)
      return next(method, input, options)
    },
    interceptServerStreaming(next, method, input, options) {
      applyHeaders(options)
      return next(method, input, options)
    },
  }
}

function setMetaHeader(options: { meta?: unknown }, key: string, value: string): void {
  if (!options.meta) {
    options.meta = {}
  }

  const meta = options.meta as unknown

  // Some transports use a plain object for meta, but others may use Headers.
  if (typeof Headers !== "undefined" && meta instanceof Headers) {
    meta.set(key, value)
    return
  }
  ;(meta as Record<string, string>)[key] = value
}
